// @ts-nocheck
import React, { useState } from "react";
import { Camera, Search, CheckCircle, Package, AlertTriangle, ArrowLeft, Loader2, Save, X, PlusCircle, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNavigate } from "@tanstack/react-router";

interface ScannedItem {
  itemId: string;
  itemType: string;
  code: string;
  name: string;
  unit: string;
  systemQty: number;
  actualQty: number;
}

export default function MobileInventoryCheck() {
  const navigate = useNavigate();
  const [scannedCode, setScannedCode] = useState("");
  const [itemType, setItemType] = useState<"MATERIAL" | "FG" | "TECH">("MATERIAL");
  
  // Danh sách các mặt hàng đã quét trong phiên này
  const [scannedItems, setScannedItems] = useState<ScannedItem[]>([]);
  // Mặt hàng đang xem/chỉnh sửa
  const [activeItem, setActiveItem] = useState<ScannedItem | null>(null);

  const [isScanning, setIsScanning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleScan = async () => {
    if (!scannedCode.trim()) return;
    setIsScanning(true);

    try {
      const catalog = await trpcClient.inventory.getStocktakeCatalog.query({
        warehouseType: itemType,
        search: scannedCode.trim(),
      });
      const matched = catalog?.items?.[0];

      if (!matched) {
        const typeLabel =
          itemType === "MATERIAL"
            ? "Vật tư"
            : itemType === "FG"
              ? "Thành phẩm"
              : "Khuôn / Kẽm / Công cụ kỹ thuật";
        toast.error(`Không tìm thấy ${typeLabel} với mã: ${scannedCode}`);
        setIsScanning(false);
        return;
      }

      const itemId = matched.itemId;
      const code = matched.itemCode || matched.itemId;
      const name = matched.itemName;
      const unit = matched.unit || "PCS";
      const systemQty = Number(matched.systemQty || 0);

      // Kiểm tra xem đã có trong giỏ chưa
      const existing = scannedItems.find(i => i.itemId === itemId);

      const newItem: ScannedItem = {
        itemId,
        itemType: matched.itemType || itemType,
        code,
        name,
        unit,
        systemQty,
        actualQty: existing ? existing.actualQty : systemQty, // Default là systemQty nếu quét mới
      };

      setActiveItem(newItem);
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi tra cứu mã vạch");
    } finally {
      setIsScanning(false);
    }
  };

  const handleConfirmItem = () => {
    if (!activeItem) return;
    
    // Cập nhật vào mảng
    setScannedItems(prev => {
      const idx = prev.findIndex(i => i.itemId === activeItem.itemId);
      if (idx >= 0) {
        const newArr = [...prev];
        newArr[idx] = activeItem;
        return newArr;
      }
      return [activeItem, ...prev];
    });

    toast.success("Đã ghi nhận kiểm đếm");
    setActiveItem(null);
    setScannedCode("");
  };

  const handleRemoveItem = (itemId: string) => {
    setScannedItems(prev => prev.filter(i => i.itemId !== itemId));
  };

  const handleSaveCheck = async () => {
    if (scannedItems.length === 0) {
      toast.error("Chưa có mặt hàng nào được kiểm đếm");
      return;
    }
    
    setIsSaving(true);
    try {
      const payload = {
        checkDate: new Date().toISOString(),
        type: itemType,
        notes: "Kiểm đếm nhanh qua Mobile",
        lines: scannedItems.map(item => ({
          itemType: item.itemType,
          itemId: item.itemId,
          itemCode: item.code,
          itemName: item.name,
          unit: item.unit,
          systemQty: item.systemQty,
          actualQty: item.actualQty,
          diffQty: item.actualQty - item.systemQty,
          notes: ""
        }))
      };

      await trpcClient.inventory.createCheck.mutate(payload);
      toast.success("Đã lưu Phiếu Kiểm Kê thành công (NHÁP)");
      setScannedItems([]);
      setActiveItem(null);
      setScannedCode("");
      
      // Navigate back
      window.history.back();
    } catch (error: any) {
      toast.error(error.message || "Lỗi khi lưu phiếu kiểm kê");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-indigo-600 text-white p-4 shadow-sm flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => window.history.back()} className="p-1 rounded-full hover:bg-indigo-700">
          <ArrowLeft className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-semibold flex-1">Kiểm kê nhanh</h1>
        <Badge variant="outline" className="bg-indigo-700/50 text-indigo-50 border-indigo-400">
          Mobile Check
        </Badge>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-4 flex flex-col gap-4 max-w-lg mx-auto w-full mb-20">
        
        {/* Tab Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-2">
          <Tabs defaultValue={itemType} onValueChange={(v) => setItemType(v as any)}>
            <TabsList>
              <TabsTrigger value="MATERIAL">Kho Vật Tư</TabsTrigger>
              <TabsTrigger value="FG">Kho Thành Phẩm</TabsTrigger>
              <TabsTrigger value="TECH">Kho Kỹ Thuật</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Scan Section */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex flex-col gap-3">
          <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
            <Camera className="w-4 h-4 text-indigo-600" />
            Quét mã vạch / QR
          </label>
          <div className="flex gap-2">
            <Input 
              placeholder="Nhập hoặc quét mã..." 
              className="h-12 text-lg text-center font-mono uppercase bg-slate-50 focus-visible:ring-indigo-500"
              value={scannedCode}
              onChange={(e) => setScannedCode(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleScan();
              }}
              disabled={isScanning}
              autoFocus
            />
            <Button 
              size="icon" 
              className="h-12 w-12 bg-indigo-600 hover:bg-indigo-700 shrink-0 rounded-xl"
              onClick={handleScan}
              disabled={isScanning || !scannedCode}
            >
              {isScanning ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Active Scan Result */}
        {activeItem && (
          <div className="flex flex-col gap-4 animate-in fade-in slide-in-from-bottom-4 duration-300">
            <Card className="border-indigo-100 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs font-semibold text-indigo-600 uppercase tracking-wider mb-1">
                      {activeItem.code}
                    </div>
                    <h2 className="text-lg font-bold text-slate-800 leading-tight line-clamp-2">
                      {activeItem.name}
                    </h2>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setActiveItem(null)} className="h-8 w-8 text-slate-400 hover:bg-slate-100 shrink-0">
                    <X className="w-5 h-5" />
                  </Button>
                </div>

                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex justify-between items-center mb-4">
                  <div className="text-sm text-slate-500 font-medium">Tồn hệ thống</div>
                  <div className="text-xl font-bold font-mono text-slate-800">
                    {activeItem.systemQty.toLocaleString()} <span className="text-sm font-normal text-slate-500">{activeItem.unit}</span>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <label className="text-sm font-bold text-slate-700">Tồn thực tế kiểm đếm</label>
                  <div className="flex items-center gap-2">
                    <Input 
                      type="number"
                      className="h-16 text-3xl font-bold font-mono text-center text-emerald-700 bg-emerald-50 border-emerald-200 focus-visible:ring-emerald-500"
                      value={activeItem.actualQty === 0 && activeItem.systemQty !== 0 ? "" : activeItem.actualQty}
                      onChange={(e) => setActiveItem({...activeItem, actualQty: Number(e.target.value)})}
                      onClick={(e) => e.currentTarget.select()}
                    />
                  </div>
                  {activeItem.actualQty !== activeItem.systemQty && (
                    <div className="flex items-center gap-1.5 text-amber-600 text-sm mt-2 font-medium bg-amber-50 p-2 rounded-md">
                      <AlertTriangle className="w-4 h-4" />
                      Chênh lệch: {(activeItem.actualQty - activeItem.systemQty).toLocaleString()} {activeItem.unit}
                    </div>
                  )}
                </div>

                <Button 
                  className="w-full h-12 text-base font-bold bg-indigo-600 hover:bg-indigo-700 rounded-xl"
                  onClick={handleConfirmItem}
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Ghi nhận (Đã quét: {scannedItems.length})
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Scanned Items List */}
        {!activeItem && scannedItems.length > 0 && (
          <div className="flex flex-col gap-3">
            <h3 className="font-semibold text-slate-700 flex items-center justify-between">
              <span>Đã kiểm đếm ({scannedItems.length})</span>
            </h3>
            {scannedItems.map((item) => (
              <Card key={item.itemId} className="shadow-sm">
                <CardContent className="p-4 flex items-center justify-between gap-3">
                  <div className="flex-1 overflow-hidden">
                    <div className="font-mono text-xs text-slate-500">{item.code}</div>
                    <div className="font-medium text-sm truncate">{item.name}</div>
                    <div className="text-xs text-slate-500 mt-1 flex gap-3">
                      <span>HT: {item.systemQty}</span>
                      <span className={item.actualQty !== item.systemQty ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                        TT: {item.actualQty}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setActiveItem(item)}>
                      <PlusCircle className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="icon" className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleRemoveItem(item.itemId)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!activeItem && scannedItems.length === 0 && (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 p-8 text-center space-y-3">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-2">
              <Package className="w-8 h-8 text-slate-300" />
            </div>
            <p>Bắt đầu quét mã để kiểm đếm số lượng thực tế.</p>
          </div>
        )}
      </main>

      {/* Footer Actions */}
      {scannedItems.length > 0 && !activeItem && (
        <div className="p-4 bg-white border-t border-slate-100 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] fixed bottom-0 left-0 right-0 z-20">
          <div className="max-w-lg mx-auto w-full">
            <Button 
              className="w-full h-14 text-lg font-bold bg-emerald-600 hover:bg-emerald-700 rounded-xl shadow-lg shadow-emerald-200"
              onClick={handleSaveCheck}
              disabled={isSaving}
            >
              {isSaving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}
              Lưu Phiếu Kiểm Kê
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
